import { useEffect, useRef, useState } from "react";
import { Search, UserPlus } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const tl = (n) => (n ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const RISK_UI = { A: "bg-emerald-100 text-emerald-800", B: "bg-amber-100 text-amber-800", C: "bg-red-100 text-red-700" };

export default function SmartLookup({ kind = "product", onSelect, placeholder, testid = "smart-lookup", className = "" }) {
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState([]);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const timer = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const onChange = (v) => {
    setQ(v);
    clearTimeout(timer.current);
    if (v.trim().length < 2) { setOpts([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/admin/lookup/${kind === "cari" ? "cari" : "products"}`, { params: { q: v.trim() } });
        setOpts(data.items || []);
        setOpen(true);
      } catch { setOpts([]); }
    }, 250);
  };

  const idOf = (o) => o.product_id || o.cari_id;

  const quickCreateCari = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const { data } = await api.post("/admin/cari", { name: q.trim(), type: "musteri" });
      toast.success(`"${data.name}" cari olarak eklendi`);
      onSelect(data);
      setQ(""); setOpts([]); setOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Cari eklenemedi"); }
    finally { setCreating(false); }
  };

  return (
    <div ref={boxRef} className={`relative ${className}`}>
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
        <input data-testid={`${testid}-input`} className="h-8 pl-8 pr-2 rounded-lg border border-stone-200 text-xs bg-stone-50 w-64 max-w-full focus:outline-none focus:ring-2 focus:ring-forest/30 focus:bg-white" placeholder={placeholder} value={q} onChange={(e) => onChange(e.target.value)} onFocus={() => opts.length && setOpen(true)} />
      </div>
      {open && opts.length > 0 && (
        <div data-testid={`${testid}-dropdown`} className="absolute z-40 mt-1 w-80 max-w-[92vw] bg-white border border-stone-200 rounded-xl shadow-xl max-h-64 overflow-y-auto">
          {opts.map((o) => (
            <button type="button" key={idOf(o)} data-testid={`${testid}-opt-${idOf(o)}`}
              onMouseDown={() => { onSelect(o); setQ(""); setOpts([]); setOpen(false); }}
              className="w-full text-left px-3 py-2 hover:bg-forest/5 border-b border-stone-50 last:border-0 transition-colors">
              {kind === "product" ? (
                <>
                  <span className="flex justify-between gap-2 text-xs">
                    <span className="font-medium text-stone-800 truncate">{o.name}</span>
                    <span className={`shrink-0 font-semibold ${o.stock <= 0 ? "text-red-500" : "text-emerald-700"}`}>stok: {o.stock}</span>
                  </span>
                  <span className="block text-[10px] text-stone-400 font-mono mt-0.5">{o.sku || o.barcode || "—"} · {tl(o.discount_price ?? o.price)} ₺{o.base_unit && o.base_unit !== "adet" ? `/${o.base_unit}` : ""}</span>
                </>
              ) : (
                <>
                  <span className="flex justify-between gap-2 text-xs">
                    <span className="font-medium text-stone-800 truncate">{o.name}{o.company ? ` (${o.company})` : ""}</span>
                    {o.risk && <span className={`shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${RISK_UI[o.risk.grade] || ""}`}>{o.risk.grade}</span>}
                  </span>
                  <span className="block text-[10px] text-stone-400 mt-0.5">{o.type_label || ""}{o.phone ? ` · ${o.phone}` : ""} · Bakiye: {tl(o.balance)} ₺</span>
                </>
              )}
            </button>
          ))}
        </div>
      )}
      {open && !opts.length && q.trim().length >= 2 && (
        <div className="absolute z-40 mt-1 w-72 max-w-[92vw] bg-white border border-stone-200 rounded-xl shadow-xl px-3 py-2.5 text-xs text-stone-400">
          {kind === "cari" ? (
            <button type="button" data-testid={`${testid}-quick-add`} onMouseDown={quickCreateCari} disabled={creating}
              className="w-full flex items-center gap-2 text-left text-forest font-semibold hover:bg-forest/5 rounded-lg px-2 py-1.5 -mx-1 transition-colors disabled:opacity-50">
              <UserPlus className="w-3.5 h-3.5 shrink-0" />
              {creating ? "Ekleniyor..." : <span className="truncate">"{q.trim()}" yeni cari olarak ekle</span>}
            </button>
          ) : "Sonuç bulunamadı"}
        </div>
      )}
    </div>
  );
}
